import { useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ArrowLeft,
  Sparkles,
  Play,
  Send,
  Flame,
} from "lucide-react";

// ============================================================
// Category Destination Pages — `/trucking`, `/motorcycles`,
// `/boating`, `/cars`. Each renders the same 5-section structure
// (Hero → Featured Originals → Currently Casting → Culture
// Gallery → Final CTA) fed by the CATEGORY config below.
//
// Show CTAs route into the existing intake/apply system:
//   - Dedicated pages: /the-set-speaks, /booked-outside
//   - Modal-driven flows: navigate to `/?apply=<action>` and the
//     Landing page picks it up and opens the right modal/flow.
// ============================================================

const IMG_HIGHWAY = "/cultures/highway-legends.png";
const IMG_RIDEOUT = "/cultures/the-rideout.png";
const IMG_LAKE = "/cultures/lake-life.png";
const IMG_STREET = "/cultures/street-machines.png";

const POSTER = {
  builtNotBought:
    "https://customer-assets.emergentagent.com/job_branding-guide-demo/artifacts/3v2xh8sr_copy_D0ADCA47-6C49-46C0-91CB-90516C8F432F.jpeg",
  cultureFiles:
    "https://customer-assets.emergentagent.com/job_branding-guide-demo/artifacts/rry9s4g2_copy_38C57C12-14AB-42C0-9C65-BB7D859309F5.jpeg",
  weOutside:
    "https://customer-assets.emergentagent.com/job_branding-guide-demo/artifacts/d2395beg_A5884D80-C4CF-4EC1-9EEB-ACBDA8777EA8.png",
  reenactment:
    "https://customer-assets.emergentagent.com/job_branding-guide-demo/artifacts/iox4fxnq_copy_6499631C-6D83-46D5-A4E7-EA195691B7E5.jpeg",
  streetEats:
    "https://customer-assets.emergentagent.com/job_branding-guide-demo/artifacts/tx22cspx_IMG_8590.png",
  stage:
    "https://customer-assets.emergentagent.com/job_branding-guide-demo/artifacts/xubdvo1j_117F3023-0ADA-49D2-869F-0F10D3E4BDF0.png",
  creators:
    "https://customer-assets.emergentagent.com/job_branding-guide-demo/artifacts/e955el2o_28F03F70-8EC4-4458-9589-B440F423EC78.png",
  setSpeaks:
    "https://customer-assets.emergentagent.com/job_branding-guide-demo/artifacts/fsq1ep9o_102609F8-756D-4BDE-920D-A04F846FDB7E.png",
  bookedOutside: "/booked-outside-hero.png",
  fastLane:
    "https://customer-assets.emergentagent.com/job_branding-guide-demo/artifacts/tstxxcvb_63366FCD-3430-4F13-B40B-E09872280990.png",
};

const CATEGORIES = {
  trucking: {
    slug: "trucking",
    label: "Highway Legends",
    eyebrow: "/ AUTOMOTOVILLE CATEGORY · TRUCKING",
    blurb:
      "Long nights. Big roads. Real drivers keeping America moving. Where the truck-stop becomes a stage and the rig becomes a home.",
    hero: IMG_HIGHWAY,
    glow: "rgba(255,45,156,0.45)",
    accent: "#FF2D9C",
    featured: [
      {
        title: "Highway Legends",
        line: "The drivers, the rigs, the road — told by the people living it.",
        poster: IMG_HIGHWAY,
        casting: true,
        applyAction: "culture-files",
      },
      {
        title: "Booked & Outside",
        line: "Truckers, travelers, agents — building the culture's global passport.",
        poster: POSTER.bookedOutside,
        casting: true,
        applyHref: "/booked-outside",
      },
      {
        title: "Culture Files · Trucking",
        line: "Real stories from women in trucking, vets, and family rigs.",
        poster: POSTER.cultureFiles,
        casting: true,
        applyAction: "culture-files",
      },
    ],
    casting: [
      {
        kind: "Drivers & Owner-Operators",
        line: "Diesel veterans, new women in trucking, family fleets.",
        applyAction: "culture-files",
      },
      {
        kind: "Truck-Stop Personalities",
        line: "Cooks, mechanics, lot lizards' lounges, characters at the pump.",
        applyAction: "culture-files",
      },
      {
        kind: "Events & Truck Shows",
        line: "Conventions, big rig meets, pride parades.",
        applyAction: "events",
      },
    ],
    gallery: [
      IMG_HIGHWAY,
      POSTER.bookedOutside,
      POSTER.cultureFiles,
      POSTER.weOutside,
      POSTER.reenactment,
      IMG_HIGHWAY,
    ],
  },
  motorcycles: {
    slug: "motorcycles",
    label: "The Rideout",
    eyebrow: "/ AUTOMOTOVILLE CATEGORY · MOTORCYCLES",
    blurb:
      "More than bikes. Brotherhood. Sisterhood. Respect. Freedom. The clubs, the rideouts, the patches — and the people behind them.",
    hero: IMG_RIDEOUT,
    glow: "rgba(0,229,255,0.45)",
    accent: "#00E5FF",
    featured: [
      {
        title: "The Set Speaks",
        line: "MC & SC voices — the patch holders finally tell it themselves.",
        poster: POSTER.setSpeaks,
        casting: true,
        applyHref: "/the-set-speaks",
      },
      {
        title: "Lipstick, Leather & Lashes",
        line: "Women who ride — bikers, biker wives, queens of the rideout.",
        poster: IMG_RIDEOUT,
        casting: true,
        applyAction: "culture-files",
      },
      {
        title: "We Outside · Ride-Outs",
        line: "Annuals, anniversaries, takeovers — if it's lit, we pull up.",
        poster: POSTER.weOutside,
        casting: true,
        applyAction: "events",
      },
    ],
    casting: [
      {
        kind: "MC / SC Members & Officers",
        line: "Patches, presidents, OGs — sit down with The Set Speaks.",
        applyHref: "/the-set-speaks",
      },
      {
        kind: "Biker Wives & Women of the Set",
        line: "Tell your side of the culture. Real. Unfiltered. On record.",
        applyAction: "culture-files",
      },
      {
        kind: "Ride-Out Events & Annuals",
        line: "Submit your event to We Outside coverage.",
        applyAction: "events",
      },
    ],
    gallery: [
      IMG_RIDEOUT,
      POSTER.setSpeaks,
      POSTER.weOutside,
      POSTER.cultureFiles,
      POSTER.reenactment,
      IMG_RIDEOUT,
    ],
  },
  boating: {
    slug: "boating",
    label: "Lake Life",
    eyebrow: "/ AUTOMOTOVILLE CATEGORY · BOATING",
    blurb:
      "Weekend takeovers. Lake nights. Waterfront nightlife. Luxury escape energy and the lifestyle that floats different.",
    hero: IMG_LAKE,
    glow: "rgba(0,229,255,0.50)",
    accent: "#00E5FF",
    featured: [
      {
        title: "Lake Life",
        line: "Sun-soaked culture, captains, and the floating block parties.",
        poster: IMG_LAKE,
        casting: true,
        applyAction: "culture-files",
      },
      {
        title: "We Outside · Lake Editions",
        line: "Boat parties, marina takeovers, holiday cookouts on the water.",
        poster: POSTER.weOutside,
        casting: true,
        applyAction: "events",
      },
      {
        title: "Booked & Outside",
        line: "The travel-agent reality where the destination is the lifestyle.",
        poster: POSTER.bookedOutside,
        casting: true,
        applyHref: "/booked-outside",
      },
    ],
    casting: [
      {
        kind: "Boat Captains & Lake Hosts",
        line: "Tell us where you summer and who you bring out.",
        applyAction: "culture-files",
      },
      {
        kind: "Lake & Marina Events",
        line: "Submit your event to We Outside.",
        applyAction: "events",
      },
      {
        kind: "Lifestyle Travel Personalities",
        line: "Apply to Booked & Outside.",
        applyHref: "/booked-outside",
      },
    ],
    gallery: [
      IMG_LAKE,
      POSTER.weOutside,
      POSTER.bookedOutside,
      POSTER.cultureFiles,
      POSTER.fastLane,
      IMG_LAKE,
    ],
  },
  cars: {
    slug: "cars",
    label: "Street Machines",
    eyebrow: "/ AUTOMOTOVILLE CATEGORY · CARS",
    blurb:
      "Built in garages. Shown in the streets. Driven like art. Horsepower, craftsmanship, and culture through machines.",
    hero: IMG_STREET,
    glow: "rgba(255,45,156,0.45)",
    accent: "#FF2D9C",
    featured: [
      {
        title: "Built Not Bought",
        line: "Builders, fabricators, custom shops — the ones who do it themselves.",
        poster: POSTER.builtNotBought,
        casting: true,
        applyAction: "built-not-bought",
      },
      {
        title: "Street Machines",
        line: "Meets, takeovers, garage doc — the cars that became the culture.",
        poster: IMG_STREET,
        casting: true,
        applyAction: "culture-files",
      },
      {
        title: "Independent Creators",
        line: "Car-culture filmmakers, vloggers, and finished projects.",
        poster: POSTER.creators,
        casting: true,
        applyAction: "content-creators",
      },
    ],
    casting: [
      {
        kind: "Builders, Fabricators & Garages",
        line: "Submit your build for Built Not Bought.",
        applyAction: "built-not-bought",
      },
      {
        kind: "Car Meets & Street Events",
        line: "Submit your event to We Outside.",
        applyAction: "events",
      },
      {
        kind: "Drivers & Stunt Personalities",
        line: "Tell your story for Culture Files.",
        applyAction: "culture-files",
      },
    ],
    gallery: [
      IMG_STREET,
      POSTER.builtNotBought,
      POSTER.creators,
      POSTER.weOutside,
      POSTER.cultureFiles,
      IMG_STREET,
    ],
  },
};

// Legacy slug map — /culture/:slug routes still in the wild get
// folded into the new top-level routes.
const LEGACY_SLUGS = {
  "highway-legends": "trucking",
  "the-rideout": "motorcycles",
  "lake-life": "boating",
  "street-machines": "cars",
};

export function categoryRedirectFromLegacy(slug) {
  return LEGACY_SLUGS[slug];
}

export default function CategoryPage() {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  // Top-level routes like /trucking, /motorcycles, /boating, /cars
  // come through as pathname slug. Legacy /culture/:slug uses params.slug.
  const pathSlug = location.pathname.replace(/^\//, "").split("/")[0];
  const slug = CATEGORIES[pathSlug]
    ? pathSlug
    : params.slug
    ? LEGACY_SLUGS[params.slug]
    : null;
  const data = slug ? CATEGORIES[slug] : null;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  // For deep-linking back into Landing modals (?apply=<action>).
  const goApply = useMemo(
    () => (item) => {
      if (item.applyHref) {
        navigate(item.applyHref);
        return;
      }
      if (item.applyAction) {
        navigate(`/?apply=${encodeURIComponent(item.applyAction)}`);
        return;
      }
      navigate("/");
    },
    [navigate]
  );

  if (!data) {
    return <Navigate to="/" replace />;
  }

  return (
    <div
      className="relative min-h-screen bg-[#05050a] text-white overflow-x-hidden"
      data-testid={`category-page-${slug}`}
    >
      <Hero data={data} onApply={() => goApply({ applyAction: "culture-files" })} />
      <FeaturedOriginals data={data} onApply={goApply} />
      <CurrentlyCasting data={data} onApply={goApply} />
      <CultureGallery data={data} />
      <FinalCTA data={data} onJoin={() => goApply({ applyAction: "culture-files" })} />

      <div className="px-6 pb-10 pt-2 text-center font-accent text-[10px] tracking-[0.32em] text-white/30">
        AUTOMOTOVILLE · {new Date().getFullYear()}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------
// Section 1 — Hero
// ----------------------------------------------------------------
function Hero({ data, onApply }) {
  const navigate = useNavigate();
  return (
    <section className="relative h-[88vh] min-h-[620px] w-full overflow-hidden">
      <div
        className="absolute inset-0 bg-cover bg-center scale-110 blur-2xl opacity-70 saturate-150"
        style={{ backgroundImage: `url(${data.hero})` }}
        aria-hidden
      />
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${data.hero})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/65 to-black/15" />
      {/* Animated neon edge lighting */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        initial={{ opacity: 0.35 }}
        animate={{ opacity: [0.35, 0.7, 0.35] }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        style={{
          background: `linear-gradient(90deg, ${data.glow} 0%, rgba(0,0,0,0) 30%, rgba(0,0,0,0) 70%, ${data.glow} 100%)`,
          mixBlendMode: "screen",
        }}
      />

      <button
        type="button"
        onClick={() => navigate("/")}
        data-testid="category-page-back"
        className="absolute top-6 left-6 z-20 inline-flex items-center gap-2 px-3 py-2 border border-white/15 bg-black/40 backdrop-blur-sm text-white/85 font-accent text-[10px] tracking-[0.28em] hover:bg-white/10 transition-colors"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> AUTOMOTOVILLE
      </button>

      <div className="relative z-10 h-full max-w-6xl mx-auto px-6 flex flex-col justify-end pb-14 md:pb-20">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.65 }}
        >
          <div
            className="font-accent text-[11px] sm:text-xs tracking-[0.32em] mb-4"
            style={{ color: data.accent }}
          >
            {data.eyebrow}
          </div>
          <h1
            className="font-heading uppercase font-black leading-[0.92] tracking-tight text-5xl md:text-7xl lg:text-8xl max-w-5xl"
            style={{
              color: "#F6EBD4",
              textShadow:
                "0 4px 32px rgba(0,0,0,0.6), 0 0 1px rgba(255,255,255,0.5)",
            }}
          >
            {data.label}
          </h1>
          <p className="mt-5 max-w-2xl text-base md:text-lg text-white/80 leading-relaxed">
            {data.blurb}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={() =>
                document
                  .getElementById("featured-originals")
                  ?.scrollIntoView({ behavior: "smooth", block: "start" })
              }
              data-testid="category-hero-explore"
              className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#FF2D9C] to-[#00E5FF] text-white font-black px-7 py-3.5 uppercase tracking-[0.28em] text-xs shadow-[0_10px_30px_-12px_rgba(255,45,156,0.7)] hover:scale-[1.03] active:scale-[0.97] transition-transform"
            >
              <Play className="w-4 h-4" /> Explore Shows
            </button>
            <button
              type="button"
              onClick={onApply}
              data-testid="category-hero-apply"
              className="inline-flex items-center justify-center gap-2 border border-white/25 text-white font-black px-7 py-3.5 uppercase tracking-[0.28em] text-xs hover:bg-white/5 active:scale-[0.97] transition-all"
            >
              <Send className="w-4 h-4" /> Apply / Submit
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ----------------------------------------------------------------
// Section 2 — Featured Originals
// ----------------------------------------------------------------
function FeaturedOriginals({ data, onApply }) {
  return (
    <section
      id="featured-originals"
      className="relative px-5 sm:px-8 py-20 md:py-24"
    >
      <div className="max-w-6xl mx-auto">
        <SectionEyebrow color="#00E5FF" text="/ FEATURED ORIGINALS" />
        <SectionHeadline>Originals from this world</SectionHeadline>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
          {data.featured.map((s, idx) => (
            <motion.button
              key={s.title}
              type="button"
              onClick={() => onApply(s)}
              data-testid={`category-featured-${idx}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.45, delay: idx * 0.06 }}
              whileHover={{ y: -4 }}
              whileTap={{ scale: 0.98 }}
              className="group relative text-left bg-[#0c0a10]/80 backdrop-blur-sm border border-white/10 overflow-hidden hover:border-white/25 transition-colors"
            >
              <div className="relative aspect-[3/4] overflow-hidden bg-black">
                <div
                  className="absolute inset-0 bg-cover bg-center scale-110 blur-xl opacity-60"
                  style={{ backgroundImage: `url(${s.poster})` }}
                  aria-hidden
                />
                <img
                  src={s.poster}
                  alt={s.title}
                  loading="lazy"
                  className="relative w-full h-full object-contain transition-transform duration-500 group-hover:scale-[1.04]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent pointer-events-none" />
                {s.casting && (
                  <span
                    className="absolute top-3 left-3 font-accent text-[10px] tracking-[0.28em] px-2.5 py-1 backdrop-blur-md border"
                    style={{
                      color: data.accent,
                      borderColor: `${data.accent}66`,
                      background: `${data.accent}1A`,
                    }}
                  >
                    CURRENTLY CASTING
                  </span>
                )}
                {/* Hover glow */}
                <div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{
                    boxShadow: `inset 0 0 60px ${data.accent}55`,
                  }}
                />
              </div>
              <div className="p-4 sm:p-5">
                <h3
                  className="font-heading font-black uppercase tracking-tight text-lg sm:text-xl leading-tight"
                  style={{ color: "#F6EBD4" }}
                >
                  {s.title}
                </h3>
                <p className="mt-1.5 text-sm text-white/65 leading-snug">
                  {s.line}
                </p>
                <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-accent tracking-[0.28em] text-white/70 group-hover:text-white">
                  {s.casting ? "APPLY NOW" : "EXPLORE"}
                  <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </div>
            </motion.button>
          ))}
        </div>
      </div>
    </section>
  );
}

// ----------------------------------------------------------------
// Section 3 — Currently Casting
// ----------------------------------------------------------------
function CurrentlyCasting({ data, onApply }) {
  return (
    <section className="relative px-5 sm:px-8 py-20 md:py-24 bg-[#08070c]">
      <div className="absolute inset-0 opacity-30 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 15% 25%, ${data.accent}22, transparent 50%), radial-gradient(circle at 85% 75%, rgba(0,229,255,0.15), transparent 55%)`,
          }}
        />
      </div>
      <div className="relative max-w-6xl mx-auto">
        <SectionEyebrow color={data.accent} text="/ CURRENTLY CASTING" />
        <SectionHeadline>Step into the culture.</SectionHeadline>
        <p className="mt-4 max-w-2xl text-sm sm:text-base text-white/65 leading-relaxed">
          Creators, personalities, performers, event hosts, builders, drivers,
          riders, and storytellers — if you live this, we want to hear from
          you.
        </p>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
          {data.casting.map((c, idx) => (
            <motion.div
              key={c.kind}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.4, delay: idx * 0.05 }}
              className="relative bg-[#0c0a10]/70 border border-white/10 p-5 sm:p-6 hover:border-white/25 transition-colors group"
            >
              <div className="flex items-start gap-3">
                <span
                  className="mt-1 inline-flex w-9 h-9 rounded-full items-center justify-center"
                  style={{
                    background: `linear-gradient(135deg, ${data.accent}, #00E5FF)`,
                    boxShadow: `0 0 24px ${data.accent}66`,
                  }}
                >
                  <Flame className="w-4 h-4 text-white" />
                </span>
                <div className="flex-1">
                  <h3 className="font-heading font-black uppercase tracking-tight text-base sm:text-lg leading-tight text-white">
                    {c.kind}
                  </h3>
                  <p className="mt-1.5 text-sm text-white/65 leading-snug">
                    {c.line}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => onApply(c)}
                data-testid={`category-casting-apply-${idx}`}
                className="mt-5 w-full inline-flex items-center justify-between gap-2 border border-white/15 bg-white/[0.03] hover:bg-white/[0.08] hover:border-white/30 px-4 py-2.5 font-heading font-black text-[11px] tracking-[0.22em] uppercase text-white/85 transition-colors"
              >
                <span>Apply Now</span>
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ----------------------------------------------------------------
// Section 4 — Culture Gallery
// ----------------------------------------------------------------
function CultureGallery({ data }) {
  return (
    <section className="relative px-5 sm:px-8 py-20 md:py-24">
      <div className="max-w-6xl mx-auto">
        <SectionEyebrow color="#00E5FF" text="/ CULTURE GALLERY" />
        <SectionHeadline>This is what it looks like.</SectionHeadline>

        <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
          {data.gallery.map((src, idx) => (
            <motion.div
              key={`${src}-${idx}`}
              initial={{ opacity: 0, scale: 0.96 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.45, delay: idx * 0.04 }}
              className={`relative overflow-hidden bg-black border border-white/10 group ${
                idx === 0
                  ? "col-span-2 md:col-span-2 aspect-[16/10]"
                  : "aspect-square"
              }`}
            >
              <div
                className="absolute inset-0 bg-cover bg-center scale-110 blur-2xl opacity-60"
                style={{ backgroundImage: `url(${src})` }}
                aria-hidden
              />
              <img
                src={src}
                alt=""
                loading="lazy"
                className="relative w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent pointer-events-none" />
              <div
                className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{
                  boxShadow: `inset 0 0 40px ${data.accent}55`,
                }}
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ----------------------------------------------------------------
// Section 5 — Final CTA
// ----------------------------------------------------------------
function FinalCTA({ data, onJoin }) {
  const navigate = useNavigate();
  return (
    <section className="relative px-5 sm:px-8 py-24 md:py-32 overflow-hidden">
      <div
        className="absolute inset-0 opacity-50"
        style={{
          backgroundImage: `radial-gradient(circle at 25% 35%, ${data.accent}33, transparent 50%), radial-gradient(circle at 75% 65%, rgba(0,229,255,0.22), transparent 55%)`,
        }}
      />
      <motion.div
        className="absolute inset-0 pointer-events-none"
        initial={{ opacity: 0.25 }}
        animate={{ opacity: [0.25, 0.55, 0.25] }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        style={{
          background:
            "linear-gradient(90deg, rgba(255,45,156,0.18), transparent 50%, rgba(0,229,255,0.18))",
          mixBlendMode: "screen",
        }}
      />
      <div className="relative max-w-3xl mx-auto text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.55 }}
        >
          <div className="font-accent text-[11px] sm:text-xs tracking-[0.32em] text-white/55 mb-5">
            / AUTOMOTOVILLE ORIGINALS
          </div>
          <h2
            className="font-heading uppercase font-black leading-[1.02] tracking-tight text-4xl sm:text-5xl md:text-6xl"
            style={{ color: "#F6EBD4" }}
          >
            This culture
            <br />
            <span
              style={{
                background:
                  "linear-gradient(90deg, #FF2D9C, #00E5FF)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              never stops.
            </span>
          </h2>
          <p className="mt-6 text-base sm:text-lg text-white/70 leading-relaxed max-w-xl mx-auto">
            AutoMotoVille is building the next generation of culture-driven
            originals. Pull up — there's a lane for you.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              type="button"
              onClick={onJoin}
              data-testid="category-cta-join"
              className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#FF2D9C] to-[#00E5FF] text-white font-black px-8 py-3.5 uppercase tracking-[0.28em] text-xs shadow-[0_12px_36px_-12px_rgba(255,45,156,0.75)] hover:scale-[1.03] active:scale-[0.97] transition-transform"
            >
              <Sparkles className="w-4 h-4" /> Join the Movement
            </button>
            <button
              type="button"
              onClick={() => navigate("/?apply=culture-files")}
              data-testid="category-cta-story"
              className="inline-flex items-center justify-center gap-2 border border-white/25 text-white font-black px-8 py-3.5 uppercase tracking-[0.28em] text-xs hover:bg-white/5 active:scale-[0.97] transition-all"
            >
              <Send className="w-4 h-4" /> Submit Your Story
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ----------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------
function SectionEyebrow({ color, text }) {
  return (
    <div
      className="font-accent text-[11px] sm:text-xs tracking-[0.32em]"
      style={{ color }}
    >
      {text}
    </div>
  );
}

function SectionHeadline({ children }) {
  return (
    <h2
      className="mt-3 font-heading uppercase font-black leading-[1.02] tracking-tight text-3xl sm:text-4xl md:text-5xl max-w-3xl"
      style={{ color: "#F6EBD4" }}
    >
      {children}
    </h2>
  );
}
